import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { InputNumber } from "primereact/inputnumber";
import {
    addMaterial,
    updateMaterial,
    setDataNull,
} from "../../store/materials/slice";
import { Dropdown } from "primereact/dropdown";
import "../../assets/scss/theme-base/ModalAddDashbord.css";
import { Calendar } from "primereact/calendar";
import { getOrder } from "../../store/order/slice";
import { Skeleton } from "primereact/skeleton";

const ModalAddMaterials = ({ visible, onHide, toast, rowData }) => {
    const dispatch = useDispatch();

    const {
        errorInsert,
        insertData,
        updateData,
        errorUpdate,
        classificationData,
        orderData,
    } = useSelector(
        (state) => ({
            errorInsert: state.Materials.errorInsert,
            insertData: state.Materials.insertData,
            updateData: state.Materials.updateData,
            errorUpdate: state.Materials.errorUpdate,
            classificationData: state.Classifications.classificationData,
            orderData: state.Production.orderData,
        }),
        shallowEqual
    );

    // State cho Virtual Scroll
    const [factoryCodeOptions, setFactoryCodeOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [keyword, setKeyword] = useState("");
    const limit = 20; // Giới hạn 20 mục mỗi lần tải

    // Lấy dữ liệu ban đầu
    useEffect(() => {
        if (visible) {
            const timeout = setTimeout(() => {
                dispatch(
                    getOrder({
                        page: 1,
                        limit: limit,
                        current: "plan",
                        factoryCode: keyword,
                    })
                );
                setPage(1);
                setHasMore(true);
                setFactoryCodeOptions([]);
            }, 300); // trì hoãn 300ms

            return () => clearTimeout(timeout); // clear timeout nếu keyword thay đổi nhanh
        }
    }, [dispatch, visible, keyword]);

    // Cập nhật factoryCodeOptions khi orderData thay đổi
    useEffect(() => {
        if (
            orderData?.data?.products?.items &&
            orderData.data.products.items.length > 0
        ) {
            const newOptions = orderData.data.products.items.map((item) => ({
                label: item.factoryCode,
                value: item.id,
            }));
            setFactoryCodeOptions((prev) => {
                const uniqueOptions = [
                    ...(rowData?.Product?.factoryCode
                        ? [
                              {
                                  label: rowData.Product.factoryCode,
                                  value: rowData.productId,
                              },
                          ]
                        : []),
                    ...prev,
                    ...newOptions,
                ];
                // Loại bỏ trùng lặp
                const uniqueMap = new Map(
                    uniqueOptions.map((item) => [item.value, item])
                );
                return Array.from(uniqueMap.values());
            });
            setHasMore(orderData.data.products.items.length === limit);
        } else {
            setHasMore(false);
        }
        setLoading(false);
    }, [orderData, rowData]);

    // Validation schema
    const validationSchema = Yup.object().shape({
        code: Yup.string().required("Mã vật tư là bắt buộc"),
        name: Yup.string().required("Tên vật tư là bắt buộc"),
        price: Yup.number()
            .required("Giá là bắt buộc")
            .min(0, "Giá phải lớn hơn hoặc bằng 0"),
        quantity: Yup.number()
            .required("Số lượng là bắt buộc")
            .min(0, "Số lượng phải lớn hơn hoặc bằng 0"),
        classificationId: Yup.string().required("Phân loại là bắt buộc"),
        type: Yup.string().required("Loại là bắt buộc"),
        materialRequestNumber: Yup.string().required(
            "Yêu cầu vật tư là bắt buộc"
        ),
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            code: rowData?.code || "",
            name: rowData?.name || "",
            price: rowData?.price || "",
            quantity: rowData?.quantity || "",
            unit: rowData?.unit || "",
            hh: rowData?.hh || "",
            classificationId: rowData?.classificationId || "",
            type: rowData?.type || "",
            expectedArrivalDate: rowData?.expectedArrivalDate
                ? new Date(rowData.expectedArrivalDate)
                : null,
            materialRequestNumber: rowData?.materialRequestNumber || "",
            materialRequestNumberPr: rowData?.materialRequestNumberPr || "",
            productId: rowData?.productId || "",
            description: rowData?.description || "",
            status: rowData?.status || "",
        },
        validationSchema,
        onSubmit: async (value) => {
            const data = {
                ...value,
                ...(rowData?.id ? { id: rowData.id } : {}),
            };
            if (rowData?.id) {
                dispatch(updateMaterial({ data }));
            } else {
                dispatch(addMaterial({ data }));
            }
        },
    });

    // Xử lý toast thông báo
    useEffect(() => {
        if (errorInsert) {
            toast.current?.show({
                severity: "error",
                summary: "Thất bại",
                detail: errorInsert?.response?.data?.message,
                life: 3000,
            });
            dispatch(setDataNull());
        }
        if (insertData) {
            toast.current?.show({
                severity: "success",
                summary: "Thành công",
                detail: "Thêm vật tư thành công",
                life: 3000,
            });
            formik.resetForm();
            dispatch(setDataNull());
            onHide();
        }
    }, [errorInsert, insertData, dispatch, formik, onHide, toast]);

    useEffect(() => {
        if (errorUpdate) {
            toast.current?.show({
                severity: "error",
                summary: "Thất bại",
                detail: errorUpdate?.response?.data?.message,
                life: 3000,
            });
            dispatch(setDataNull());
        }
        if (updateData) {
            toast.current?.show({
                severity: "success",
                summary: "Thành công",
                detail: "Cập nhật vật tư thành công",
                life: 3000,
            });
            formik.resetForm();
            dispatch(setDataNull());
            onHide();
        }
    }, [errorUpdate, updateData, dispatch, formik, onHide, toast]);

    // Hàm xử lý Lazy Load cho Virtual Scroll
    const onLazyLoad = (event) => {
        if (!hasMore || loading) return;

        const { last } = event;

        if (last >= factoryCodeOptions.length - 5 && hasMore) {
            setLoading(true);
            dispatch(
                getOrder({
                    page: page + 1,
                    limit: limit,
                    current: "plan",
                })
            );
            setPage((prev) => prev + 1);
        }
    };

    // Template cho trạng thái đang tải
    const loadingTemplate = (options) => (
        <div style={{ height: "38px", display: "flex", alignItems: "center" }}>
            <Skeleton width={options.even ? "60%" : "50%"} height="1.3rem" />
        </div>
    );

    return (
        <Dialog
            visible={visible}
            onHide={() => {
                formik.resetForm();
                onHide();
            }}
            header="Vật tư"
            modal
            style={{ height: "84%", width: "40%" }}
            className="responsive-dialog"
            footer={
                <div>
                    <Button
                        label="Hủy"
                        onClick={() => {
                            formik.resetForm();
                            onHide();
                        }}
                        severity="secondary"
                        className="p-button-text"
                    />
                    <Button
                        type="submit"
                        label="Lưu"
                        onClick={formik.handleSubmit}
                    />
                </div>
            }
        >
            <div className="bg-white p-3 border-round-md">
                <div className="flex flex-column mb-4 gap-3">
                    <div className="flex justify-content-between align-items-center col-12 gap-5">
                        <div className="flex flex-column w-6">
                            <label className="mb-2">Mã vật tư</label>
                            <InputText
                                id="code"
                                name="code"
                                value={formik.values.code}
                                onChange={formik.handleChange}
                                style={{ height: "40px" }}
                                className={
                                    formik.touched.code && formik.errors.code
                                        ? "p-invalid"
                                        : ""
                                }
                            />
                            {formik.touched.code && formik.errors.code && (
                                <div style={{ color: "red" }}>
                                    {formik.errors.code}
                                </div>
                            )}
                        </div>
                        <div className="flex flex-column w-6">
                            <label className="mb-2">Tên vật tư</label>
                            <InputText
                                id="name"
                                name="name"
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                style={{ height: "40px" }}
                                className={
                                    formik.touched.name && formik.errors.name
                                        ? "p-invalid"
                                        : ""
                                }
                            />
                            {formik.touched.name && formik.errors.name && (
                                <div style={{ color: "red" }}>
                                    {formik.errors.name}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex justify-content-between align-items-center col-12 gap-5">
                        <div className="flex flex-column w-6">
                            <label className="mb-2">YCVT số BRAVO</label>
                            <InputText
                                id="materialRequestNumber"
                                name="materialRequestNumber"
                                value={formik.values.materialRequestNumber}
                                onChange={formik.handleChange}
                                style={{ height: "40px" }}
                                className={
                                    formik.touched.materialRequestNumber &&
                                    formik.errors.materialRequestNumber
                                        ? "p-invalid"
                                        : ""
                                }
                            />
                            {formik.touched.materialRequestNumber &&
                                formik.errors.materialRequestNumber && (
                                    <div style={{ color: "red" }}>
                                        {formik.errors.materialRequestNumber}
                                    </div>
                                )}
                        </div>
                        <div className="flex flex-column w-6">
                            <label className="mb-2">YCVT số PR</label>
                            <InputText
                                id="materialRequestNumberPr"
                                name="materialRequestNumberPr"
                                value={formik.values.materialRequestNumberPr}
                                onChange={formik.handleChange}
                                style={{ height: "40px" }}
                            />
                        </div>
                    </div>
                    <div className="flex justify-content-between align-items-center col-12 gap-5">
                        <div className="flex flex-column w-6">
                            <label className="mb-2">Giá</label>
                            <InputNumber
                                id="price"
                                name="price"
                                value={formik.values.price}
                                onValueChange={(e) =>
                                    formik.setFieldValue("price", e.value)
                                }
                                mode="decimal"
                                min={0}
                                style={{ height: "40px", width: "100%" }}
                                className={
                                    formik.touched.price && formik.errors.price
                                        ? "p-invalid"
                                        : ""
                                }
                            />
                            {formik.touched.price && formik.errors.price && (
                                <div style={{ color: "red" }}>
                                    {formik.errors.price}
                                </div>
                            )}
                        </div>
                        <div className="flex flex-column w-6">
                            <label className="mb-2">Số lượng</label>
                            <InputNumber
                                id="quantity"
                                name="quantity"
                                value={formik.values.quantity}
                                onValueChange={(e) =>
                                    formik.setFieldValue("quantity", e.value)
                                }
                                mode="decimal"
                                min={0}
                                style={{ height: "40px", width: "100%" }}
                                className={
                                    formik.touched.quantity &&
                                    formik.errors.quantity
                                        ? "p-invalid"
                                        : ""
                                }
                            />
                            {formik.touched.quantity &&
                                formik.errors.quantity && (
                                    <div style={{ color: "red" }}>
                                        {formik.errors.quantity}
                                    </div>
                                )}
                        </div>
                    </div>
                    <div className="flex justify-content-between align-items-center col-12 gap-5">
                        <div className="flex flex-column w-6">
                            <label className="mb-2">Đơn vị tính</label>
                            <InputText
                                id="unit"
                                name="unit"
                                value={formik.values.unit}
                                onChange={formik.handleChange}
                                style={{ height: "40px" }}
                            />
                        </div>
                        <div className="flex flex-column w-6">
                            <label className="mb-2">HH</label>
                            <InputText
                                id="hh"
                                name="hh"
                                value={formik.values.hh}
                                onChange={formik.handleChange}
                                style={{ height: "40px" }}
                            />
                        </div>
                    </div>
                    <div className="flex justify-content-between align-items-center col-12 gap-5">
                        <div className="flex flex-column w-6">
                            <label className="mb-2">Phân loại</label>
                            <Dropdown
                                id="classificationId"
                                name="classificationId"
                                value={formik.values.classificationId}
                                options={
                                    Array.isArray(
                                        classificationData?.data?.items
                                    )
                                        ? classificationData.data.items.map(
                                              (item) => ({
                                                  label: item.name,
                                                  value: item.id,
                                              })
                                          )
                                        : []
                                }
                                filter
                                onChange={(e) =>
                                    formik.setFieldValue(
                                        "classificationId",
                                        e.value
                                    )
                                }
                                style={{
                                    height: "40px",
                                    width: "100%",
                                    marginTop: "5px",
                                }}
                                className={
                                    formik.touched.classificationId &&
                                    formik.errors.classificationId
                                        ? "p-invalid"
                                        : ""
                                }
                            />
                            {formik.touched.classificationId &&
                                formik.errors.classificationId && (
                                    <div style={{ color: "red" }}>
                                        {formik.errors.classificationId}
                                    </div>
                                )}
                        </div>
                        <div className="flex flex-column w-6">
                            <label className="mb-2">Loại</label>
                            <Dropdown
                                id="type"
                                name="type"
                                value={formik.values.type}
                                options={[
                                    { label: "Nguyên vật liệu", value: "NVL" },
                                    { label: "Gia công", value: "GC" },
                                ]}
                                filter
                                onChange={(e) =>
                                    formik.setFieldValue("type", e.value)
                                }
                                style={{
                                    height: "40px",
                                    width: "100%",
                                    marginTop: "5px",
                                }}
                                className={
                                    formik.touched.type && formik.errors.type
                                        ? "p-invalid"
                                        : ""
                                }
                            />
                            {formik.touched.type && formik.errors.type && (
                                <div style={{ color: "red" }}>
                                    {formik.errors.type}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex justify-content-between align-items-center col-12 gap-5">
                        <div className="flex flex-column w-6">
                            <label className="mb-2">Ngày về dự kiến</label>
                            <Calendar
                                id="expectedArrivalDate"
                                name="expectedArrivalDate"
                                value={formik.values.expectedArrivalDate}
                                onChange={(e) =>
                                    formik.setFieldValue(
                                        "expectedArrivalDate",
                                        e.value
                                    )
                                }
                                dateFormat="dd/mm/yy"
                                showIcon
                                style={{ height: "40px", width: "100%" }}
                            />
                        </div>
                        <div className="flex flex-column w-6">
                            <label className="mb-2">Trạng thái</label>
                            <Dropdown
                                id="status"
                                name="status"
                                value={formik.values.status}
                                options={[
                                    { label: "Chưa đặt", value: "Chưa đặt" },
                                    { label: "Đã đặt", value: "Đã đặt" },
                                    { label: "Chưa về", value: "Chưa về" },
                                    { label: "Đã về", value: "Đã về" },
                                    { label: "SDTK", value: "SDTK" },
                                ]}
                                filter
                                onChange={(e) =>
                                    formik.setFieldValue("status", e.value)
                                }
                                style={{
                                    height: "40px",
                                    width: "100%",
                                    marginTop: "5px",
                                }}
                            />
                        </div>
                    </div>
                    <div className="flex justify-content-between align-items-center col-12 gap-5">
                        <div className="flex flex-column w-6">
                            <label className="mb-2">Mã nhà máy</label>
                            <Dropdown
                                id="productId"
                                name="productId"
                                value={formik.values.productId}
                                options={factoryCodeOptions}
                                onChange={(e) =>
                                    formik.setFieldValue("productId", e.value)
                                }
                                onFilter={(e) => setKeyword(e.filter)}
                                filter
                                filterBy="label"
                                virtualScrollerOptions={{
                                    lazy: true,
                                    onLazyLoad,
                                    itemSize: 38,
                                    showLoader: true,
                                    delay: 100,
                                    loadingTemplate,
                                }}
                                style={{
                                    height: "40px",
                                    width: "100%",
                                    border: "1px solid #ddd",
                                }}
                            />
                        </div>
                        <div className="flex flex-column w-6">
                            <label className="mb-2">Ghi chú</label>
                            <InputText
                                id="description"
                                name="description"
                                value={formik.values.description}
                                onChange={formik.handleChange}
                                style={{ height: "40px" }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default ModalAddMaterials;
